<template>
  <div
    class="relative h-8 select-none"
    :class="{
      'w-40': size === 'regular',
      'w-24': size === 'small',
      'w-16': size === 'x-small',
      'w-full': size === 'full',
    }"
  >
    <div
      class="border-solid border rounded-lg h-full flex items-center"
      :class="{
        'cursor-pointer': !disabled,
        'cursor-default': disabled,
        'rounded-b-none': open,
        'bg-secondary text-secondary-invert border-secondary-light w-full': isDark,
        'border-grey-200': !isDark,
      }"
      @click="toggle"
    >
      <span
        class="select-none pl-4 truncate pr-6"
        :class="{
          'text-grey-200': placeholderShown && !isDark,
          'text-secondary-invert': placeholderShown && isDark,
        }"
        >{{ text }}</span
      >
    </div>
    <span :class="{ 'text-grey-500': !isDark, 'text-secondary-invert': isDark }">
      <arrow-down
        class="h-4 w-4 fill-current absolute top-0 mt-2 right-0 mr-2"
        @click="toggle"
      ></arrow-down>
    </span>
    <transition name="fade" mode="out-in">
      <VuePerfectScrollbar
        v-if="open"
        v-on-click-outside="closeMenu"
        class="
          border-solid border-l border-r border-b
          rounded-lg rounded-t-none
          shadow-lg
          relative
          z-10
          w-full
          custom-scrollbars
        "
        :class="{
          'bg-secondary': isDark,
          'bg-white': !isDark,
          'border-grey-200': !isDark,
          'border-secondary-light': isDark,
          'max-h-full': compress,
        }"
        :style="{ maxHeight }"
      >
        <div
          v-if="placeholder"
          :class="dropdownClass({}, 0)"
          class="italic"
          @click="select({ value: null })"
        >
          {{ placeholder }}
        </div>
        <div
          v-for="(action, index) in actions"
          :key="index"
          :class="{
            'px-4 hover:bg-secondary-darker text-secondary-invert hover:text-secondary-invert select-none': isDark,
            'px-4 hover:bg-secondary-darker hover:text-secondary-invert select-none': !isDark,
            'border-b border-secondary-light': isDark && index === actions.length - 1,
            'border-b border-grey-200': isDark && index === actions.length - 1,
          }"
          @click="callAction(action.callback)"
        >
          {{ action.text }}
        </div>
        <template v-if="styled">
          <div
            v-for="(option, index) in formattedOptions"
            :key="option.text"
            :class="dropdownClass(option, index)"
            :style="getStyle(styled, option)"
            @click="select(option)"
          >
            {{ option.text }}
          </div>
        </template>
        <template v-else>
          <div
            v-for="(option, index) in formattedOptions"
            :key="option.value"
            :class="dropdownClass(option, index)"
            @click="select(option)"
          >
            {{ option.text }}
          </div>
        </template>
      </VuePerfectScrollbar>
    </transition>
  </div>
</template>

<script>
import ArrowDown from '@fortawesome/fontawesome-free/svgs/solid/angle-down.svg';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import directives from '@groovepages/gd-ui-kit/directives';
import mixins from '@groovepages/gd-ui-kit/mixins';

export default {
  name: 'DropdownWithScroll',
  components: {
    ArrowDown,
    VuePerfectScrollbar
  },
  directives,
  mixins: [mixins],
  props: {
    compress: {},
    value: {},
    placeholder: {},
    options: {},
    dark: {},
    size: { default: 'regular' },
    actions: {
      default: () => [],
    },
    isGrouped: null,
    styled: null,
    maxHeight: { default: '400px' },
    disabled: Boolean,
  },
  data() {
    return { open: false };
  },
  computed: {
    isDark() {
      return this.hasState('dark');
    },
    placeholderShown() {
      if (!this.value) return true;
      const value = this.formattedOptions.find(
        (option) => option.value === this.value
      );
      if (!value) return true;
      return false;
    },
    text() {
      const placeholder = this.placeholder || '-';
      let value;
      if (this.styled) {
        value = this.formattedOptions.find(
          (option) => option.value.name === this.value
        );
      } else {
        value = this.formattedOptions.find(
          (option) => option.value === this.value
        );
      }

      return value ? value.text : placeholder;
    },
    formattedOptions() {
      if (!this.options) return [];
      if (this.options[0] && this.options[0].value) return this.options;
      return this.options.map((option) => ({ text: option, value: option }));
    },
  },
  methods: {
    getStyle(styled, option) {
      return { [styled]: option.value.name };
    },
    callAction(action) {
      this.open = false;
      action();
    },
    toggle() {
      this.open = this.disabled ? false : !this.open;
    },
    select(option) {
      this.open = false;
      this.$emit('input', option.value);
      this.$emit('change', option.value);
      this.$emit('changeValue', option.value);
    },
    closeMenu() {
      this.open = false;
    },
    dropdownClass(option, index) {
      let classList = '';
      if (option.value === this.value) classList = 'font-bold ';
      if (index === this.formattedOptions.length - 1)
        classList += 'rounded-lg rounded-t-none ';
      if (this.isDark) {
        return `${classList}px-4 py-2 hover:bg-secondary-darker text-secondary-invert hover:text-secondary-invert select-none`;
      }
      return `${classList}px-4 py-2 hover:bg-secondary-darker hover:text-secondary-invert select-none`;
    },
  },
};
</script>

<style>
.custom-max-h-64 {
  max-height: 16rem;
}
</style>
